import { isEmpty } from 'lodash/fp';
import { flow, pipe } from 'fp-ts/function';
import { string, boolean, array, ord, option } from 'fp-ts';
import { createSelector } from 'reselect';
import { Address } from '@mews-commander/data-access';
import { getOrEmptyString } from '@mews/fp';
import { mapId } from '@mews-commander/utils';
import { getAddressesByOwnerId, getAddressesByIds } from '@mews-commander/legacy-entities/src/ducks/addresses';
import { getSelectedEntityId } from '../entityManagement/selectors';
import { sortByCreatedUtcDescending } from '../entityManagement/utils';

interface WithIsComplete extends Address {
    isComplete: boolean;
}

const byIsComplete = pipe(
    boolean.Ord,
    ord.contramap((a: WithIsComplete) => a.isComplete)
);

const byUpdatedUtc = pipe(
    // @ts-expect-error
    string.Ord,
    ord.contramap((a: Address) => a.updatedUtc)
);

export const getBestAddress = flow(
    getAddressesByOwnerId,
    array.map((a: Address) => ({
        ...a,
        isComplete: !isEmpty(a.addressLine1) && !isEmpty(a.city) && !isEmpty(a.countryCode),
    })),
    array.sortBy([byIsComplete, byUpdatedUtc]),
    array.last
);

const getPrimaryAddressId = flow(
    getBestAddress,
    option.match(
        () => null,
        (a) => a.id
    )
);

export const getSelectedAddressesByIds = createSelector(
    getAddressesByIds,
    getPrimaryAddressId,
    (addresses, primaryAddressId) =>
        array.map((a: Address) => ({
            ...a,
            isPrimary: primaryAddressId === a.id,
        }))(addresses)
);

export const getOwnerSortedAddressIds = flow(getAddressesByOwnerId, sortByCreatedUtcDescending, mapId);

export const getSelectedAddress = createSelector(
    getAddressesByOwnerId,
    getSelectedEntityId,
    (addresses: Array<Address>, id: string) =>
        pipe(
            addresses,
            array.findFirst((a) => a.id === id),
            option.toUndefined
        )
);

export const getSelectedAddressFormValues = createSelector(getSelectedAddress, (address: Address | undefined) => {
    if (address === undefined) return null;

    const { addressLine1, addressLine2, city, postalCode, countryCode, countrySubdivisionCode } = address;

    return {
        addressLine1: addressLine1 ?? null,
        addressLine2: addressLine2 ?? null,
        city: city ?? null,
        postalCode: postalCode ?? null,
        countryCode: countryCode ?? null,
        countrySubdivisionCode: countrySubdivisionCode ?? null,
    };
});

export const getSelectedAddressLine1 = createSelector(getSelectedAddress, getOrEmptyString('addressLine1'));
